import { useLayoutEffect, useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Eye,
  EyeOff,
  LockKeyhole,
  ShieldCheck
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { userErrorMessage } from "../lib/api";
import { Button, FieldError } from "../components/ui";
import { fieldErrorId, useFormErrors } from "../lib/form-errors";
import { LoginTeamGallery } from "../features/login/LoginTeamGallery";
import { initialTheme } from "../lib/theme";
import helptechLogo from "../assets/login-team/helptech-logo.png";
import "../features/login/login.css";

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const formErrors = useFormErrors();

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = initialTheme();
  }, []);

  if (user) {
    void navigate({
      to: user.mustChangePassword ? "/change-password" : "/inbox"
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    formErrors.clear();
    try {
      const loggedUser = await login({ email, password });
      await navigate({
        to: loggedUser.mustChangePassword ? "/change-password" : "/inbox"
      });
    } catch (cause) {
      if (!formErrors.capture(cause)) {
        setError(userErrorMessage(cause, "Não foi possível entrar."));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-page login-team-page">
      <section className="login-showcase">
        <div className="login-brand">
          <img
            className="login-official-logo"
            src={helptechLogo}
            alt="HelpTech — Soluções para todas as tecnologias"
            width={720}
            height={240}
            decoding="async"
            draggable={false}
          />
        </div>
        <LoginTeamGallery email={email} />
        <div className="showcase-footer">
          <ShieldCheck size={16} />
          Conectado com segurança à Meta Cloud API
        </div>
      </section>

      <section className="login-form-area">
        <form className="login-form" onSubmit={submit}>
          <span className="login-lock">
            <LockKeyhole size={22} />
          </span>
          <span className="eyebrow">Bem-vindo ao HTZAP</span>
          <h2>Acesse o WhatsApp corporativo</h2>
          <label className="field">
            <span>Email</span>
            <input
              name="email"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                formErrors.clear("email");
              }}
              aria-invalid={Boolean(formErrors.errors.email)}
              aria-describedby={formErrors.errors.email ? fieldErrorId("email") : undefined}
              autoComplete="email"
              required
            />
            <FieldError field="email" message={formErrors.errors.email} />
          </label>
          <label className="field">
            <span>Senha</span>
            <div className="password-field">
              <input
                name="password"
                type={visible ? "text" : "password"}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  formErrors.clear("password");
                }}
                aria-invalid={Boolean(formErrors.errors.password)}
                aria-describedby={formErrors.errors.password ? fieldErrorId("password") : undefined}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setVisible((value) => !value)}
                aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
              >
                {visible ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <FieldError field="password" message={formErrors.errors.password} />
          </label>
          {error ? <div className="form-error">{error}</div> : null}
          <Button type="submit" disabled={loading} className="login-submit">
            {loading ? "Entrando..." : "Entrar no HTZAP"}
            <ArrowRight size={18} />
          </Button>
          <small className="login-help">
            Problemas para acessar? Fale com o administrador do workspace.
          </small>
        </form>
      </section>
    </main>
  );
}
